import { FhirBundleSdk } from "@metriport/fhir-sdk";
import { useMemo } from "react";
import type { ColumnDef } from "../../components/base-table-layout";
import { createEhrWriteBackColumn } from "../../ehr/create-ehr-column";
import { useEhrContext } from "../../../shared-logic/ehr-context/ehr-context";
import { BaseTableLayout } from "../../components/base-table-layout";
import { CodingBadge } from "../../components/coding-badge";
import { TableRow } from "../../utils/tables-metadata";
import { MedicationRowData } from "./medication-rows";

export type MedicationTableProps = {
  rows: TableRow[];
  onRowClick: (row: TableRow) => void;
  fhirSdk: FhirBundleSdk;
  filterQuery: string;
  onPdfView?: (docId: string) => void;
  displayName?: string;
  isRowViewed?: (row: TableRow) => boolean;
};

export function MedicationTable({
  rows,
  onRowClick,
  filterQuery,
  displayName = "Medications",
  isRowViewed,
}: MedicationTableProps) {
  const ehrContext = useEhrContext();

  const columns = useMemo(() => {
    const cols: ColumnDef<MedicationRowData>[] = [
      { key: "medication", header: "Medication", maxWidth: "300px" },
      { key: "dose", header: "Dose", width: { base: "70px", md: "120px" } },
      {
        key: "quantity",
        header: "Quantity",
        isHiddenOnSmallScreen: true,
        width: { base: "70px", md: "120px" },
      },
      {
        key: "code",
        header: "Code",
        render: (row, query) => <CodingBadge coding={row.coding} searchQuery={query} />,
        isHiddenOnSmallScreen: true,
      },
      {
        key: "lastFillDate",
        header: "Last Fill Date",
        width: "120px",
        isHiddenOnSmallScreen: true,
      },
      { key: "date", header: "Date", width: "120px", sort: "desc" },
    ];

    const ehrColumn = createEhrWriteBackColumn<MedicationRowData>(ehrContext, "medication");
    if (ehrColumn) cols.push(ehrColumn);

    return cols;
  }, [ehrContext]);

  function handleRowClick(row: TableRow) {
    onRowClick(row);
  }

  return (
    <BaseTableLayout
      title={displayName}
      rows={rows as unknown as MedicationRowData[]}
      columns={columns}
      filterQuery={filterQuery}
      onRowClick={handleRowClick}
      isRowViewed={isRowViewed}
    />
  );
}
